/**
 * 3-card KPI section (Quality / Delivery / Financial) for Cached Performance
 * tab (CR-4751). Mirrors KPIMetricsSection visual language from DefaultTab.
 */
import type {
  DashboardSnapshot, MetricType, PeriodType, TrendPoint,
} from '../../../lib/api/dashboard-snapshots'
import { CachedKPICell } from './CachedKPICell'
import { DAILY_HIDDEN_NOTE } from './helpers'

interface CachedKPISectionProps {
  latestByMetric: Map<MetricType, DashboardSnapshot>
  trends: Record<MetricType, TrendPoint[]>
  period?: PeriodType
  /** Period metrics with nothing to measure in the latest capture (CR-4751). */
  naMetrics?: Set<MetricType>
}

export function CachedKPISection({
  latestByMetric, trends, period = 'monthly', naMetrics = new Set<MetricType>(),
}: CachedKPISectionProps) {
  const cell = (metric: MetricType) => ({
    snapshot: latestByMetric.get(metric),
    trend: trends[metric],
    notApplicable: naMetrics.has(metric),
  })
  const daily = period === 'daily'

  return (
    <div className="bg-card rounded-lg shadow-sm p-4">
      {/* CR-4751: the three cards stack below xl — side by side, each money
          figure had ~45px at a 1,030px window and the amounts ran together. */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-stretch">
        {/* Quality */}
        <div className="border border-red-200 bg-red-50/30 rounded-lg p-4 flex flex-col">
          <h3 className="text-center font-semibold text-red-600 mb-4 text-sm uppercase tracking-wide">Quality</h3>
          <div className="grid grid-cols-2 gap-3 items-start flex-1">
            <CachedKPICell label="PPM" {...cell('ppm')} lowerIsBetter emphasis="destructive" />
            <CachedKPICell label="NCRs" {...cell('ncr_count')} lowerIsBetter />
          </div>
        </div>

        {/* Delivery — CR-4751: a single day does not measure OTIF or OTD. */}
        <div className="border border-green-200 bg-green-50/30 rounded-lg p-4 flex flex-col">
          <h3 className="text-center font-semibold text-green-600 mb-4 text-sm uppercase tracking-wide">Delivery</h3>
          <div className={`grid ${daily ? 'grid-cols-2' : 'grid-cols-3'} gap-3 items-start flex-1`}>
            {daily ? (
              <p className="text-xs text-muted-foreground text-center self-center">{DAILY_HIDDEN_NOTE}</p>
            ) : (
              <>
                <CachedKPICell label="OTIF" {...cell('otif_percentage')} />
                <CachedKPICell label="OTD" {...cell('otd_percentage')} />
              </>
            )}
            <CachedKPICell label="Appeals" {...cell('appeals_count')} lowerIsBetter />
          </div>
        </div>

        {/* Financial */}
        <div className="border border-blue-200 bg-blue-50/30 rounded-lg p-4 flex flex-col">
          <h3 className="text-center font-semibold text-blue-600 mb-4 text-sm uppercase tracking-wide">Financial</h3>
          <div className="grid grid-cols-3 gap-3 items-start flex-1">
            <CachedKPICell label="Open Orders" {...cell('open_order_value')} />
            <CachedKPICell label="Total Spend" {...cell('total_spend')} />
            <CachedKPICell label="Arrears" {...cell('arrears_value')} lowerIsBetter />
          </div>
        </div>
      </div>
    </div>
  )
}
